import { computed, observable } from 'mobx'
import Communicator from '../Communicator'
import { IUserResponse } from '../models'
import { IStores } from '../stores'
import ApplicationModel from '../models/ApplicationModel'
import communicate from '../shared/decorators/communicator'
import GenevaWrapper from '../shared/custom/geneva-wrapper'

const loginStatus = { true: 'true', false: 'false', unknown: 'unknown' }

@communicate(Communicator.getInstance())
export default class ApplicationStore {
  store: IStores
  initCommunicator

  @observable collection = []
  @observable language: string = 'de'
  @observable loggedInStatus: string = loginStatus.unknown
  @observable applicationLanguage: string = 'de-DE'
  @observable showSidebar: boolean = true
  @observable token: string = ''
  @observable pageId: number = null
  @observable firstname: string = ''
  @observable lastname: string = ''
  @observable tokenPageId: number = null
  @observable showScreenshotIndicator: boolean = false
  @observable iframeUrl: string = ''
  @observable pageChanged: boolean
  @observable requestIframeUpdate: boolean = false


  initialParams: string[]

  @computed get userName(): string {
    if (this.firstname === '' && this.lastname === '') {
      return 'Unknown'
    }
    return `${this.firstname} ${this.lastname}`
  }

  @computed get languageWithoutRegionCode() {
    // Split locales with a region code
    return this.applicationLanguage.toLowerCase().split(/[_-]+/)[0]
  }

  // to see the store name when in production mode - Uglifyjs
  static storeName = 'ApplicationStore'

  constructor(store: IStores) {
    this.store = store

    // Define user's language. Different browsers have the user locale defined
    // on different fields on the `navigator` object, so we make sure to account
    // for these different by checking all of them
    this.applicationLanguage = (navigator.languages && navigator.languages[0])
      || navigator.language
    this.initCommunicator()
  }

  getById(id: number) {
    return this.collection.find((item) => {
      return item.id === id
    })
  }

  // necessary since the backend response is as a string
  setPageId(id: any): void {
    if (id) {
      this.pageId = id * 1
    }
  }

  setUser(user: IUserResponse) {
    const comm = Communicator.getInstance()

    Object.assign(this, user.data)
    this.tokenPageId = user.data.pageId
    comm.setUser({
      firstname: user.data.firstname,
      id: user.data.userId,
      lastname: user.data.lastname
    })

    this.setPageId(user.data.pageId)
  }

  // should only be done after login
  setRoom() {
    const comm = Communicator.getInstance()
    comm.setRoom({ room: this.tokenPageId})
  }

  setLanguage(userLang) {
    this.language = userLang
  }

  // Swap show and hide on side bar
  switchSideBarView() {
    if (this.showScreenshotIndicator) {
      return
    }
    
    this.showSidebar = !this.showSidebar
    // doesn't matter on direction, clear hidden messages
    this.store.reviewStore.hiddenMessageCount = 0
  }

  /**
   * display - true is in screenshot mode
   * was not able to have this just be a swap because of duplicate calls from key presses
   */
  showScreenshotIndicatorView(display: boolean) {
    this.showScreenshotIndicator = display
    // Also hide sidebar
    this.showSidebar = !display
    // doesn't matter on direction, clear hidden messages
    this.store.reviewStore.hiddenMessageCount = 0
  }

  // applications are like: WM, MM, MS, etc....
  loadApplications(applications) {
    this.collection = []
    applications.forEach((application) => {
      const item = new ApplicationModel(this.store, application)
      this.collection.push(item)
    })
  }

  flagPageChanged(data) {
    if (data && data.pageId === this.pageId) {
      this.pageChanged = true
    }
  }

  setLoginStatus(newState: string) {
    if (loginStatus[newState]) {
      this.loggedInStatus = loginStatus[newState]
    }
  }

  /**
   * Used for notifying the backend that a message has been seen
   */
  updateLastVisted() {
    GenevaWrapper.put(
      `review/${this.pageId}/updateLastVisited`,
      {},
      {
        token: this.token,
        successCallback: () => {},
        errorCallback: (res: IUserResponse) => {
          console.warn('unable to update backend on message viewed', res)
        }
      }
    )
  }

  toJS() {
    return this.collection.map(item => item.toJS())
  }

  static fromJS(array, pstore) {
    const store = new ApplicationStore(pstore)
    store.collection = array.map(item => ApplicationModel.fromJS(pstore, item))
    return store
  }
}
